// Copyright ©️ 2026 eVolve MEP, LLC

import type { ReactNode } from 'react';

import { Button, Stack, Flex } from '@mantine/core';

import { EULA_MODAL_Z_INDEX } from 'app/EULAModal';
import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { useUserPreferences } from 'hooks/typeCacheHooks/useUserPreferences/useUserPreferences';

// Welcome modals must appear above every other modal except the EULA.
const WELCOME_MODAL_Z_INDEX = EULA_MODAL_Z_INDEX - 1;

type Props = {
  children: ReactNode;
  /**
   * Unique feature name for this welcome screen.
   * @remarks Changing this value will cause the screen to reappear for all users!
   */
  featureName: string;
};

export const WelcomeModal = ({ children, featureName }: Props) => {
  const [hasViewed, setHasViewed, loading] = useUserPreferences(`WELCOME_SCREEN_${featureName}`, false);

  return (
    <WrappedModal
      zIndex={WELCOME_MODAL_Z_INDEX}
      title=""
      opened={!hasViewed && !loading}
      onClose={() => setHasViewed(true)}
      hideModalButtons
      hideHeader
      size="md"
    >
      <Stack>
        {children}

        <Flex>
          <Button fullWidth onClick={() => setHasViewed(true)} autoFocus data-autofocus>
            Get started
          </Button>
        </Flex>
      </Stack>
    </WrappedModal>
  );
};
